import { useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { authApi, formatApiError } from "../lib/api";
import { Camera, Loader2, Eye, EyeOff, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

/**
 * Public page reached via the email link "Redefinir senha".
 * Reads the one-time token from the URL query string, lets the user pick a
 * new password, then redirects to /login on success.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FFFBF7] flex items-center justify-center p-4">
        <div className="bg-white border-2 border-[#1C1917] rounded-xl shadow-[6px_6px_0px_#1C1917] max-w-sm w-full p-6 text-center space-y-3">
          <div className="w-12 h-12 mx-auto bg-red-600 border-2 border-[#1C1917] rounded-full flex items-center justify-center shadow-[3px_3px_0px_#1C1917]">
            <AlertTriangle className="w-6 h-6 text-white" strokeWidth={2.5} />
          </div>
          <p className="text-sm font-bold text-[#1C1917]">Link invalido</p>
          <p className="text-xs text-[#78716C]">
            O link que voce abriu esta incompleto. Solicite um novo na tela de login.
          </p>
          <Link
            to="/login"
            className="inline-block bg-[#1C1917] text-white border-2 border-[#1C1917] rounded-full font-bold text-sm px-4 py-2 mt-2"
          >
            Voltar para o login
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (password.length < 6) {
      toast.error("A senha precisa ter ao menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      toast.error("As senhas nao conferem.");
      return;
    }
    setSaving(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
      toast.success("Senha redefinida com sucesso!");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF7] flex items-center justify-center p-4">
      <div className="max-w-sm w-full">
        <div className="flex items-center gap-2 justify-center mb-6">
          <div className="w-10 h-10 bg-[#FF6B6B] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shadow-[3px_3px_0px_#1C1917]">
            <Camera className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-black text-2xl tracking-tight text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Festa.IA
          </span>
        </div>

        <div className="bg-white border-2 border-[#1C1917] rounded-xl shadow-[6px_6px_0px_#1C1917] p-6">
          {done ? (
            <div className="text-center space-y-3 py-2">
              <div className="w-12 h-12 mx-auto bg-[#22C55E] border-2 border-[#1C1917] rounded-full flex items-center justify-center shadow-[3px_3px_0px_#1C1917]">
                <CheckCircle2 className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <p className="text-sm font-bold text-[#1C1917]">Senha redefinida!</p>
              <p className="text-xs text-[#78716C]">Voce sera redirecionado para o login...</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <h1 className="text-xl font-black text-[#1C1917] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Criar nova senha
              </h1>
              <p className="text-xs text-[#78716C] leading-snug mb-2">
                Escolha uma senha forte com pelo menos 6 caracteres.
              </p>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] font-bold text-[#78716C] uppercase tracking-wider">Nova senha</Label>
                  <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    className="flex items-center gap-1 text-xs font-bold text-[#FF6B6B] hover:underline"
                    data-testid="reset-show-btn"
                  >
                    {show ? <><EyeOff className="w-3.5 h-3.5" /> Ocultar</> : <><Eye className="w-3.5 h-3.5" /> Mostrar</>}
                  </button>
                </div>
                <Input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua nova senha"
                  required
                  minLength={6}
                  className="mt-1 border-2 border-[#1C1917] rounded-lg"
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <Label className="text-[11px] font-bold text-[#78716C] uppercase tracking-wider">Confirmar senha</Label>
                <Input
                  type={show ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repita a senha"
                  required
                  minLength={6}
                  className="mt-1 border-2 border-[#1C1917] rounded-lg"
                  data-testid="reset-confirm-input"
                />
              </div>
              <Button
                type="submit"
                disabled={saving || !password || !confirm}
                className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all disabled:opacity-60"
                data-testid="reset-submit-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Redefinir senha
              </Button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-[#78716C] mt-4">
          Voltar para{" "}
          <Link to="/login" className="text-[#FF6B6B] font-bold hover:underline">
            login
          </Link>
        </p>
      </div>
    </div>
  );
}
